import { ReactNode } from 'react';
import { generateTrackingId } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { useIsCompactMode } from '@frontend/responsiveness';
import { SurfaceHeader } from '@frontend/surface-header';
import { ScreenLayoutHeader } from './screen-layout';

type Props = {
  onClose?: () => void;
  children?: ReactNode;
  customHeader?: ScreenLayoutHeader;
  /** When true, renders a back (caret-left) button instead of the close (X) button. */
  showBackButton?: boolean;
  showCloseButton?: boolean;
};

export const CallInsightHeader = ({
  customHeader,
  onClose: handleClose,
  showBackButton,
  showCloseButton = true,
}: Props) => {
  const { t } = useTranslation('call-insight-panel');
  const isCompactMode = useIsCompactMode();
  const showStart = isCompactMode || showBackButton || customHeader?.headerStart;
  const hasActions = customHeader?.headerActions || (showCloseButton && !showBackButton && !isCompactMode);

  return (
    <SurfaceHeader.Root>
      <SurfaceHeader.Row>
        {showStart && (
          <SurfaceHeader.Start>
            {customHeader?.headerStart || <SurfaceHeader.Icon iconName='caret-left-small' onClick={handleClose} />}
          </SurfaceHeader.Start>
        )}
        <SurfaceHeader.Title>{customHeader?.headerTitle || t('Call Insights')}</SurfaceHeader.Title>
        {hasActions && (
          <SurfaceHeader.Actions>
            {customHeader?.headerActions || (
              <SurfaceHeader.Icon
                iconName='x'
                className='close-btn'
                label={t('Close panel')}
                data-testid='close-button'
                trackingId={generateTrackingId({
                  prefix: 'call-insight-panel',
                  component: 'close-btn',
                })}
                onClick={handleClose}
              />
            )}
          </SurfaceHeader.Actions>
        )}
      </SurfaceHeader.Row>
      {customHeader?.headerBody && <SurfaceHeader.Body>{customHeader?.headerBody}</SurfaceHeader.Body>}
    </SurfaceHeader.Root>
  );
};
